import React, { CSSProperties } from 'react';
import { CardTypesEnum, FiservCardStatusEnum, getDuration } from '@common-fe/common-fe';

import { ALL_OPTION, ALL_OPTION_KEY } from '@/common/constants';
import { Option, ValuesMap } from '@/common/types';
import { PaymentType } from '@/modules/transaction/transaction.types';
import { IEnrollmentDtoAccountStatusEnum } from '@/types';

import {
  CardStatusReportTableParams,
  CardStatusSummaryReportParams,
} from './ReportDetails/CardStatusReport/CardStatusReport.types';
import {
  CIPStatusReportTableParams,
  CIPStatusSummaryReportParams,
} from './ReportDetails/CIPStatusReport/CIPStatusReport.types';
import {
  ClaimsActivityReportTableParams,
  ClaimsActivitySummaryReportParams,
} from './ReportDetails/ClaimsActivityReport/ClaimsActivityReport.types';
import {
  ContributionInvoiceReportTableParams,
  ContributionInvoiceSummaryReportParams,
} from './ReportDetails/ContributionInvoiceReport/ContributionInvoiceReport.types';
import { EmployeeDemographicsReportTableParams } from './ReportDetails/EmployeeDemographicsReport/EmployeeDemographicsReport.types';
import {
  ForfeitureReportTableParams,
  ForfeitureSummaryReportParams,
} from './ReportDetails/ForfeitureReport/ForfeitureReport.types';
import {
  HealthPlanEnrollmentsReportParams,
  HealthPlanEnrollmentsReportTableParams,
} from './ReportDetails/HealthPlanEnrollmentsReport/HealthPlanEnrollmentReport.types';
import {
  ParticipantAccountActivityReportTableParams,
  ParticipantAccountActivitySummaryReportParams,
} from './ReportDetails/ParticipantActivityReport/ParticipantActivityReport.types';
import {
  PayrollContributionReportTableParams,
  PayrollContributionSummaryReportParams,
} from './ReportDetails/PayrollContributionReport/PayrollContributionReport.types';
import {
  PrefundInvoiceReportTableParams,
  PrefundInvoiceSummaryReportParams,
} from './ReportDetails/PrefundInvoiceReport/PrefundReport.types';
import {
  ReplenishmentInvoiceReportTableParams,
  ReplenishmentInvoiceSummaryReportParams,
} from './ReportDetails/ReplenishmentInvoiceReport/ReplenishmentInvoiceReport.types';
import { UndeliverableMailReportTableParams } from './ReportDetails/UndeliverableMailReport/UndeliverableMailReport.types';
export const LAST_4_DIGIT_KEY = 'last4Digits';
// export enum ReportProgram {
//   SCHEDULED = 'SCHEDULED',
//   KPI = 'KPI',
// }

export enum ActivityDateRangeFor {
  ClaimSubmitDate = 'CLAIM_SUBMIT_DATE',
  ClaimPaidDate = 'CLAIM_PAID_DATE',
}
export type FilterRecord =
  | CardStatusReportTableParams
  | CardStatusSummaryReportParams
  | CIPStatusSummaryReportParams
  | CIPStatusReportTableParams
  | ClaimsActivitySummaryReportParams
  | ClaimsActivityReportTableParams
  | ContributionInvoiceSummaryReportParams
  | ContributionInvoiceReportTableParams
  | EmployeeDemographicsReportTableParams
  | ForfeitureSummaryReportParams
  | ForfeitureReportTableParams
  | HealthPlanEnrollmentsReportParams
  | HealthPlanEnrollmentsReportTableParams
  | ParticipantAccountActivitySummaryReportParams
  | ParticipantAccountActivityReportTableParams
  | PayrollContributionSummaryReportParams
  | PayrollContributionReportTableParams
  | PrefundInvoiceSummaryReportParams
  | PrefundInvoiceReportTableParams
  | ReplenishmentInvoiceSummaryReportParams
  | ReplenishmentInvoiceReportTableParams
  | UndeliverableMailReportTableParams;

export type FilterRecordCallback = (value: FilterRecord) => void;
export interface ReportTableBaseProps {
  isPreviewMode?: boolean;
  defaultEmployerId?: string;
}
export interface ReportDetailsProps<T = void> extends ReportTableBaseProps {
  defaultTab?: ReportTab;
  defaultEmployerId?: string;
  defaultEmployerIds?: string[];
  onChangeFilters?: (values: T) => void;
  defaultFilterValues?: Partial<T>;
}
export interface ReportHookParams {
  isDebounceLess: boolean;
}
export const PLAN_YEAR_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: 'PRIOR',
    title: 'Prior',
  },
  {
    key: 'CURRENT',
    title: 'Current',
  },
  {
    key: 'FUTURE',
    title: 'Future',
  },
];

export const ACTIVITY_DATE_RANGE_FOR_OPTIONS: Option[] = [
  {
    key: ActivityDateRangeFor.ClaimSubmitDate,
    value: 'Claim submit date',
  },
  {
    key: ActivityDateRangeFor.ClaimPaidDate,
    value: 'Claim paid date',
  },
];
export const CLAIM_TYPE_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: 'REIMBURSEMENT',
    title: 'Reimbursement',
  },
  {
    key: 'BILL_PAY',
    title: 'Bill pay',
  },
  {
    key: 'CARD_SWIPE',
    title: 'Card Swipe',
  },
];

export const CLAIM_STATUSES_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: 'PROCESSING',
    title: 'Processing',
  },
  {
    key: 'NEEDS_HELP',
    title: 'Needs help',
  },
  {
    key: 'PAID',
    title: 'Paid',
  },
  {
    key: 'PARTIALLY_PAID',
    title: 'Partially paid',
  },
  {
    key: 'WAITING_FOR_FUNDS',
    title: 'Waiting for funds',
  },
  {
    key: 'PENDING',
    title: 'Pending',
  },
  {
    key: 'REPAID',
    title: 'Repaid',
  },
  {
    key: 'DENIED',
    title: 'Denied',
  },

  {
    key: 'REPROCESSING',
    title: 'Reprocessing',
  },
  {
    key: 'PARTIALLY_REPAID',
    title: 'Partially Repaid',
  },
  {
    key: 'REPAYMENT_REQUIRED',
    title: 'Repayment Required',
  },
  {
    key: 'CANCELLED',
    title: 'Cancelled',
  },
  {
    key: 'APPLIED_TO_DEDUCTIBLE',
    title: 'Applied to deductible',
  },
  {
    key: 'OUT_OF_POCKET',
    title: 'Out of pocket',
  },
  {
    key: 'COMPLETE',
    title: 'Complete',
  },
  {
    key: 'VOIDED',
    title: 'Voided',
  },
  {
    key: 'PAID_INELIGIBLE_EXPENSE',
    title: 'Paid ineligible expense',
  },
  {
    key: 'PENDING_ACCOUNT_VERIFICATION',
    title: 'Pending account verification',
  },
  {
    key: 'REPAYMENT_INITIATED',
    title: 'Repayment Initiated',
  },
];
export enum EnrollmentCipStatusEnum {
  None = 'NONE',
  Pending = 'PENDING',
  Pass = 'PASS',
  Fail = 'FAIL',
  Quest = 'QUESTION',
  Renewed = 'RENEWED',
  CipFailedAccountClosed = 'CIP_FAILED_ACCOUNT_CLOSE',
}
export const CIP_STATUS_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: EnrollmentCipStatusEnum.None,
    title: 'None',
  },
  {
    key: EnrollmentCipStatusEnum.Pending,
    title: 'Pending',
  },
  {
    key: EnrollmentCipStatusEnum.Pass,
    title: 'Pass',
  },
  {
    key: EnrollmentCipStatusEnum.Fail,
    title: 'Fail',
  },
  {
    key: EnrollmentCipStatusEnum.Quest,
    title: 'Question',
  },
  {
    key: EnrollmentCipStatusEnum.Renewed,
    title: 'Renewed',
  },
  {
    key: EnrollmentCipStatusEnum.CipFailedAccountClosed,
    title: 'CIP Failed Account Closed',
  },
];
export const ACCOUNT_TYPE_OPTIONS = [
  {
    key: ALL_OPTION_KEY,
    value: 'All',
    title: 'All',
  },
  { key: CardTypesEnum.HSA, title: 'Health Savings Account' },
  { key: CardTypesEnum.HCFSA, title: 'Health Care Flexible Spending Account' },
  { key: CardTypesEnum.HRA, title: 'Health Reimbursement Arrangement' },
  { key: CardTypesEnum.DCAP, title: 'Dependent Care Assistance Program' },
  { key: CardTypesEnum.PARKING, title: 'Parking' },
  { key: CardTypesEnum.TRANSIT, title: 'Transit' },
  { key: CardTypesEnum.ADOPTION, title: 'Adoption' },
  { key: CardTypesEnum.LIFESTYLE, title: 'Lifestyle' },
  { key: CardTypesEnum.SPECIALTY, title: 'Specialty' },
  { key: CardTypesEnum.TRAVEL, title: 'Travel Reimbursement' },
];

export const ENROLLMENT_STATUS_OPTIONS: Option[] = [
  {
    key: ALL_OPTION_KEY,
    value: 'All',
    title: 'All',
  },
  {
    key: 'ACTIVE',
    value: 'Active',
    title: 'Active',
  },
  {
    key: 'CANCELED',
    value: 'Canceled',
    title: 'Canceled',
  },
  {
    key: 'FUTURE',
    value: 'Future',
    title: 'Future',
  },
  {
    key: 'EXPIRED',
    value: 'Expired',
    title: 'Expired',
  },
];

export const ACCOUNT_STATUS_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: IEnrollmentDtoAccountStatusEnum.OPEN,
    title: 'Open',
  },
  {
    key: IEnrollmentDtoAccountStatusEnum.CLOSED,
    title: 'Closed',
  },
  {
    key: IEnrollmentDtoAccountStatusEnum.PENDING_CLOSED,
    title: 'Pending Closed',
  },
  {
    key: IEnrollmentDtoAccountStatusEnum.FROZEN,
    title: 'Frozen',
  },
  {
    key: IEnrollmentDtoAccountStatusEnum.PENDING,
    title: 'Pending',
  },
];
export const CARD_STATUSES_OPTIONS: Option[] = [
  ALL_OPTION,
  {
    key: FiservCardStatusEnum.ACTIVE,
    title: 'Active',
  },
  {
    key: FiservCardStatusEnum.CLOSED,
    title: 'Closed',
  },
  {
    key: FiservCardStatusEnum.PENDING_CLOSED,
    title: 'Pending Closed',
  },
  {
    key: FiservCardStatusEnum.PENDING_FROZEN,
    title: 'Pending Frozen',
  },
  {
    key: FiservCardStatusEnum.PENDING_OPEN,
    title: 'Pending Open',
  },
  {
    key: FiservCardStatusEnum.PENDING_REPLACE,
    title: 'Pending Replace',
  },
  {
    key: FiservCardStatusEnum.PENDING_UNLOCK,
    title: 'Pending Unlock',
  },
  {
    key: FiservCardStatusEnum.FROZEN,
    title: 'Frozen',
  },
  {
    key: FiservCardStatusEnum.LOST,
    title: 'Lost',
  },
  {
    key: FiservCardStatusEnum.STOLEN,
    title: 'Stolen',
  },
  {
    key: FiservCardStatusEnum.NOT_SENT,
    title: 'Not Sent',
  },
];
export enum ReportTypeKeys {
  CardStatus = 'CARD_STATUS',
  CIPStatus = 'CIP_STATUS',
  ClaimsActivity = 'CLAIM_ACTIVITY',
  ContributionInvoice = 'CONTRIBUTION_INVOICE',
  HealthPlanEnrollment = 'HEALTH_PLAN_ENROLLMENT',
  ParticipantActivity = 'PARTICIPANT_ACCOUNT_ACTIVITY',
  PrefundInvoiceCard = 'PREFUND_INVOICE',
  ReplenishmentInvoice = 'REPLENISHMENT_INVOICE',
  EmployeeDemographic = 'EMPLOYEE_DEMOGRAPHICS',
  PlanOmnibusBalance = 'PLAN_OMNIBUS_BALANCE',
  Forfeiture = 'FORFEITURE',
  Payroll = 'PAYROLL_CONTRIBUTIONS',
  UndeliverableAddress = 'UNDELIVERABLE_ADDRESS',
  DelinquentClaims = 'DELINQUENT_CLAIMS',
}

export enum ReportColumnType {
  Number = 'number',
  String = 'string',
  Money = 'money',
  DateTime = 'date_time',
  Date = 'date',
}
export interface ReportHeaderResponse {
  position: number;
  field_name: string;
  column_label: string;
  column_type: ReportColumnType;
}
export interface ReportPlansResponse {
  id: number;
  name: string;
}
export type ContentResponse = ValuesMap<string | number | boolean>;
export interface ReportBaseResponse {
  headers: ReportHeaderResponse[];
  plans: ReportPlansResponse[];
  last_updated_at: string;
  total_records: number;
  page_number: number;
  page_size: number;
  has_next: boolean;
}

export interface ReportSummarySubsidiaryGroupResponse {
  subsidiary_name: string;
  subsidiary_records: ContentResponse[];
}
export interface ReportSummaryContentItem {
  employer_name: string;
  employer_records: ContentResponse[];
  subsidiary_groups: ReportSummarySubsidiaryGroupResponse[];
}
export interface ReportSummaryResponse extends ReportBaseResponse {
  isWithTotal?: boolean;
  // Single employer summary: content is one object.
  // Multiple employers summary (isMultipleEmployer = true): content is an array,
  // one entry per employer.
  content: ReportSummaryContentItem | ReportSummaryContentItem[];
}
export interface ReportTableResponse extends ReportBaseResponse {
  last_updated_at: string;
  content: ContentResponse[];
}

export const REPORTS_TIMEOUT = getDuration({ seconds: 3 });

export enum ReportTypes {
  Card = 'Card',
  Invoice = 'Invoice',
  Enrollment = 'Enrollment',
  ParticipantAccountActivity = 'ParticipantAccountActivity',
  Claims = 'Claims',
  CIP = 'CIP',
  Replenishment = 'Replenishment',
  Contribution = 'Contribution',
  Prefund = 'Prefund',
  Forfeiture = 'Forfeiture',
}

export enum GroupByFilter {
  OrgField = 'ORG_FIELD',
  DateField = 'DATE_FIELD',
  Partner = 'PARTNER',
  Employer = 'EMPLOYER',
  DateOnly = 'DATE',
  PartnerAndEmployer = 'PARTNER_EMPLOYER',
  DatePartner = 'DATE_PARTNER',
  DateEmployer = 'DATE_EMPLOYER',
  DatePartnerAndEmployer = 'DATE_PARTNER_EMPLOYER',
}

export enum SortByFilter {
  OrgAsc = 'Org_ASC',
  OrgDesc = 'Org_DESC',
  DateAsc = 'Date_ASC',
  DateDesc = 'Date_DESC',
}

export enum SortByKeys {
  orgSortDirection = 'Partner/Employer',
  dateSortDirection = 'Date',
}

// interface will be changes after API integration
export interface ReportItemData {
  id: string;
  title: string;
  value?: number | string;
  prefix?: boolean;
  isTitle?: boolean;
}

export interface ReportTableItem {
  key: string;
  value: string | number;
  totalValue?: number;
  isHeader?: boolean;
  className?: string;
  style?: React.CSSProperties;
  wrapStyle?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  flex?: number;
}

export interface ReportTableRowItem {
  id: string | number;
  fields: ReportTableItem[];
}

export interface ReportItemTableData {
  header: ReportTableItem[];
  rows: ReportTableRowItem[];
  wrapStyle?: CSSProperties;
}

export enum ReportItemNestedDataRowClassNames {
  NO_PAD = 'no-pad',
  REGULAR_HOVER = 'regular-hover',
  TOTAL_HOVER = 'total-hover',
}

export interface ReportItemNestedDataRow {
  id: string;
  key?: string;
  fields: ReportTableItem[];
  style?: CSSProperties;
  lastRow?: boolean;
  classNames?: ReportItemNestedDataRowClassNames[];
}

export interface ReportItemNestedDataGroup {
  title?: string;
  id?: string;
  key?: string;
  rows?: ReportItemNestedDataRow[];
  children?: ReportItemNestedDataGroup[];
}

export interface ReportNestedItemGroup {
  title?: string;
  id?: string;
  key?: string;
  data?: ReportItemData[];
  tableData?: ReportItemTableData;
  wrapStyle?: CSSProperties;
  hierarchy?: boolean;
  children?: ReportNestedItemGroup[];
}

export interface ReportItemNestedTableData {
  groups: ReportItemNestedDataGroup[];
  wrapStyle?: CSSProperties;
}

export const REPORT_BUTTON_PORTAL_ID = 'REPORT_BUTTON_PORTAL_ID';
export const REPORT_INFO_PORTAL_ID = 'REPORT_INFO_PORTAL_ID';
export enum ReportTab {
  Summary = 'SUMMARY',
  Report = 'BREAKDOWN',
}
export interface ReportItemNestedData {
  groups: ReportNestedItemGroup[];
  mainTotalTableData?: ReportItemTableData;
}

export interface Report {
  isFilterDisplayed?: boolean;
  setFilterDisplayed?: (value: boolean) => void;
  title: string;
  type: string;
  isFullWidth?: boolean;
  showHideButtonCenter?: boolean;
  reportType?: ReportTypes;
  data?: ReportItemData[];
  nestedData?: ReportItemNestedData;
  tablesData?: ReportItemNestedData;
  tableData?: ReportItemTableData;
  nestedTableData?: ReportItemNestedTableData;
  nestedTableDataHeader?: ReportTableItem[];
  generateReportURL: string;
  id: string;
  startReportDate?: string;
  endReportDate?: string;
  tableDescription?: string;
  isWithFilter?: boolean;
  isLoading?: boolean;
  isSubLoading?: boolean;
  isEmpty?: boolean;
  isTotalUsed?: boolean;
  currentPage?: number;
  totalPages?: number;
  setCurrentPage?: (page: number) => void;
  setIsCurrentLoading?: (isLoading: boolean) => void;
}

export interface ReplenishmentInvoiceReportIndicator {
  employer_external_identifier: string;
  invoice_date: string;
  invoice_id: number;
  partner_external_identifier: string;
  total_amount_due: number;
  invoice_close_date?: string;
  employer_name?: string;
  partner_name?: string;
}

export interface CardsReportIndicator {
  active_cards_share: number;
  average_tranx: number;
  dependent_active: number;
  dependent_active_cards_share: number;
  dependent_average_tranx: number;
  dependent_issued: number;
  primary_active: number;
  primary_active_cards_share: number;
  primary_average_tranx: number;
  primary_issued: number;
  total_active: number;
  total_issued: number;
}

export interface AccountActivityReportIndicator {
  number_of_employees: number;
  avg_balance: number;
  avg_claim_spend: number;
  avg_contributions: number;
  payment_fee_amount: number;
}

export interface AccountActivityReport {
  [key: string]: AccountActivityReportIndicator;
}
export interface AccountActivityReportDto {
  account_type_summary: AccountActivityReport;
}

export interface CIPReportResponse {
  total_processed: number;
  total_passed: number;
  total_failed: number;
  passed_share: number;
  total_additional_verification_required: number;
  total_pending: number;
  total_waiting_on_response: number;
  end_report_date: string;
  start_report_date: string;
}

export interface ClaimReimbursementActivityReport {
  payment_type: PaymentType;
  plan_type: CardTypesEnum;
  count: number;
}
export interface ClaimReimbursementActivityReportDto {
  claim_reimbursement_indicators: ClaimReimbursementActivityReport[];
  total_reimbursement: number;
  total_card_swipe: number;
  total_bill_pay: number;
  end_report_date: string;
  start_report_date: string;
}

export interface ReplenishmentInvoiceReportGroup {
  group_name: string;
  indicators: (ReplenishmentInvoiceReportGroup | ReplenishmentInvoiceReportIndicator)[];
  total_amount?: number;
}

export interface CardsReportGroup {
  group_name: string;
  indicators: (CardsReportGroup | CardsReportIndicator)[];
}

export interface AccountActivityReportGroup {
  group_name: string;
  indicators: (AccountActivityReportGroup | AccountActivityReportDto)[];
}

export interface ForfeitureIndicatorPayload {
  partner_name?: string;
  employer_name?: string;
  plan_type?: string;
  forfeited_accounts_count?: number | null;
  forfeited_amount_total?: number | null;
  contribution_amount_total?: number | null;
  ineligible_claims_count?: number | null;
  ineligible_amount_total?: number | null;
  rollover_accounts_count?: number | null;
  rollover_amount_total?: number | null;
}
export interface ForfeitureReportGroup {
  group_name?: string;
  indicators?: (ForfeitureReportGroup | ForfeitureIndicatorPayload)[];
}
export interface ClaimReimbursementActivityReportGroup {
  group_name: string;
  indicators: (ClaimReimbursementActivityReportGroup | ClaimReimbursementActivityReportDto)[];
}

interface GroupReportResponse {
  end_report_date: string;
  start_report_date: string;
}

interface GroupReportResponseWithPagination extends GroupReportResponse {
  page_number: number;
  page_size: number;
  total_pages: number;
}

export interface GroupInvoiceReportResponse extends GroupReportResponseWithPagination {
  groups: ReplenishmentInvoiceReportGroup[];
}

export interface GroupCardReportResponse extends GroupReportResponseWithPagination {
  groups: CardsReportGroup[];
}

export interface GroupAccountActivityReportResponse extends GroupReportResponseWithPagination {
  groups: AccountActivityReportGroup[];
}

export interface GroupForfeitureReportResponse extends GroupReportResponseWithPagination {
  groups: ForfeitureReportGroup[];
}
export interface GroupClaimReimbursementActivityReportResponse extends GroupReportResponseWithPagination {
  groups: ClaimReimbursementActivityReportGroup[];
}

export enum InvoiceReportTableHeaderEnum {
  INVOICE_DATE = 'Invoice close date',
  INVOICE_ID = 'Invoice Id',
  TOTAL_AMOUNT_DUE = 'Total Amount Due',
}

export enum PlanYearReportFilterItems {
  NULL = 'NULL',
  PRIOR = 'PRIOR',
  CURRENT = 'CURRENT',
  FUTURE = 'FUTURE',
}

export enum DetailedBy {
  EMPLOYEE = 'EMPLOYEE',
  TRANSACTION = 'TRANSACTION',
}

export const PLAN_YEAR_FILTER_OPTIONS: Option[] = [
  {
    key: PlanYearReportFilterItems.NULL,
    value: 'All',
    title: 'All',
  },
  {
    key: PlanYearReportFilterItems.PRIOR,
    value: PlanYearReportFilterItems.PRIOR,
    title: 'Prior',
  },
  {
    key: PlanYearReportFilterItems.CURRENT,
    value: PlanYearReportFilterItems.CURRENT,
    title: 'Current',
  },
  {
    key: PlanYearReportFilterItems.FUTURE,
    value: PlanYearReportFilterItems.FUTURE,
    title: 'Future',
  },
];

export type ReportMapType = { [type: string]: Report[] };

// fields of <ReportResponse> interface will be changes after API integration
export interface ReportResponse {
  title: string;
  type: string;
  data: ReportItemData[];
  generateReportURL?: string;
  id: string;
}
